import {
  MedicareNumber,
  type IMedicareNumber,
} from '@principle-theorem/principle-core';
import { type Timestamp } from '@principle-theorem/shared';
import { compact, first, orderBy } from 'lodash';

export interface IMedicareNumberCandidate {
  /** The number as the practice recorded it. */
  value?: string;
  /** The IRN recorded beside the number, such as a health fund entry's series. */
  irn?: string;
  expiryDate?: Timestamp;
}

export interface IMedicareNumberWithExpiry extends IMedicareNumber {
  expiryDate?: Timestamp;
}

/**
 * Practices often record a patient's Medicare number as a health fund entry,
 * such as CDBS or Medicare Benefits, and each practice names those plans its
 * own way. A Medicare number carries its own check digit, so it is recognised
 * by the number itself rather than by the plan it was recorded against.
 */
export class MedicareNumberCandidate {
  /**
   * Candidates are in order of preference. The first valid number decides
   * which card the patient holds, and that card's highest issue number wins
   * because a reissued card gets the next issue number. The IRN identifies the
   * patient on the card, so it carries over from any issue of it.
   */
  static choose(
    candidates: IMedicareNumberCandidate[]
  ): IMedicareNumberWithExpiry | undefined {
    const medicareNumbers = compact(
      candidates.map((candidate) =>
        MedicareNumberCandidate._fromCandidate(candidate)
      )
    );
    const preferred = first(medicareNumbers);
    if (!preferred) {
      return;
    }

    const sameCard = medicareNumbers.filter((medicareNumber) =>
      MedicareNumber.isSameCard(medicareNumber.number, preferred.number)
    );
    const newest =
      first(
        orderBy(
          sameCard,
          [
            (medicareNumber) =>
              MedicareNumber.issueNumber(medicareNumber.number),
            (medicareNumber) => !!medicareNumber.irn,
          ],
          ['desc', 'desc']
        )
      ) ?? preferred;

    return {
      ...newest,
      irn:
        newest.irn ??
        sameCard.find((medicareNumber) => !!medicareNumber.irn)?.irn,
    };
  }

  private static _fromCandidate(
    candidate: IMedicareNumberCandidate
  ): IMedicareNumberWithExpiry | undefined {
    const medicareNumber = MedicareNumber.parse(candidate.value);
    if (!medicareNumber) {
      return;
    }
    return {
      number: medicareNumber.number,
      irn: medicareNumber.irn ?? MedicareNumber.parseIrn(candidate.irn),
      expiryDate: candidate.expiryDate,
    };
  }
}
